"use client";

import { useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { ArrowRight, Play, X, Stethoscope, Users, Brain, HeartHandshake, Check, Star, ShieldAlert, UserX, Wallet, Sparkles, Pill, UserCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
const withoutLegacyImg = "/assets/without-legacy.jpg";
const withLegacyImg = "/assets/happy-young-family-with-cute-little-daughter-havin-2026-03-19-06-52-56-utc.jpg";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import PageHero from "@/components/landing/PageHero";
import PricingCards from "@/components/landing/PricingCards";
import { AnimatedSection } from "@/hooks/useScrollAnimation";
import { makeServiceT } from "@/data/serviceDetails";
const familyHealthcare = "/assets/family-healthcare.jpg";
const familyIndividuals = "/assets/family-individuals.jpg";

type FeaturedVideo = {
  id: string;
  title: string;
  loomId: string;
  duration: string;
  Icon: typeof Stethoscope;
};

const featuredVideos: FeaturedVideo[] = [
  {
    id: "access-portal",
    title: "Explore Your Member Portal",
    loomId: "8bd7f7fad45c4b1a83b81b1a1dc8c59b",
    duration: "3 min",
    Icon: UserCheck,
  },
  {
    id: "request-doctor",
    title: "Request a Doctor Visit",
    loomId: "b7bbba2cb08e4d22b3250c4d4eb0eb95",
    duration: "3 min",
    Icon: Stethoscope,
  },
  {
    id: "prescription-savings",
    title: "Use Prescription Savings",
    loomId: "3e564c91413a484ba669dc4bf901d2bc",
    duration: "4 min",
    Icon: Pill,
  },
];

const FeaturedVideoCard = ({ video }: { video: FeaturedVideo }) => {
  const [isPlaying, setIsPlaying] = useState(false);

  return (
    <div className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 h-full flex flex-col border border-border">
      <div className="relative bg-deep-blue overflow-hidden flex-shrink-0 aspect-video">
        {isPlaying ? (
          <>
            <iframe
              src={`https://www.loom.com/embed/${video.loomId}?autoplay=1&hideEmbedTopBar=true`}
              className="absolute inset-0 w-full h-full"
              frameBorder={0}
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
              title={video.title}
            />
            <button
              onClick={() => setIsPlaying(false)}
              className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center transition-colors"
              aria-label="Close video"
            >
              <X className="w-4 h-4" />
            </button>
          </>
        ) : (
          <button
            onClick={() => setIsPlaying(true)}
            className="absolute inset-0 w-full h-full flex items-center justify-center group/play"
            aria-label={`Play ${video.title}`}
          >
            <div className="absolute inset-0 opacity-30">
              <div
                className="absolute inset-0"
                style={{
                  backgroundImage:
                    "radial-gradient(circle at 25% 30%, rgba(49,130,196,0.5) 0%, transparent 45%), radial-gradient(circle at 75% 70%, rgba(16,57,116,0.6) 0%, transparent 50%)",
                }}
              />
              <div
                className="absolute inset-0 opacity-20"
                style={{
                  backgroundImage:
                    "linear-gradient(135deg, rgba(255,255,255,0.1) 0%, transparent 50%, rgba(255,255,255,0.05) 100%)",
                }}
              />
            </div>
            <div className="absolute top-4 left-4 w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center">
              <video.Icon className="w-5 h-5 text-white/80" />
            </div>
            <div className="absolute top-4 right-4 bg-black/50 backdrop-blur-sm text-white font-button text-xs px-2.5 py-1 rounded-full">
              {video.duration}
            </div>
            <div className="relative z-10 w-16 h-16 rounded-full bg-accent-blue group-hover/play:bg-accent-blue/90 flex items-center justify-center shadow-2xl transition-all duration-300 group-hover/play:scale-110">
              <div className="absolute inset-0 rounded-full bg-accent-blue/40 animate-ping" />
              <Play className="w-7 h-7 text-white ml-1" fill="white" />
            </div>
          </button>
        )}
      </div>
      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-8 h-8 rounded-lg bg-accent-blue/10 flex items-center justify-center">
            <video.Icon className="w-4 h-4 text-accent-blue" />
          </div>
          <span className="font-button text-xs uppercase tracking-wider text-accent-blue font-semibold">
            How To
          </span>
        </div>
        <h3 className="font-heading font-bold text-base text-foreground leading-snug">
          {video.title}
        </h3>
      </div>
    </div>
  );
};

const Individuals = () => {
  const router = useRouter();
  const scrollToContact = () => { router.push("/contact"); };
  const { t } = useTranslation();

  const features = [
    {
      icon: Stethoscope,
      title: t("individuals.feature1Title"),
      description: t("individuals.feature1Desc"),
    },
    {
      icon: Users,
      title: t("individuals.feature2Title"),
      description: t("individuals.feature2Desc"),
    },
    {
      icon: Brain,
      title: t("individuals.feature3Title"),
      description: t("individuals.feature3Desc"),
    },
    {
      icon: HeartHandshake,
      title: t("individuals.feature4Title"),
      description: t("individuals.feature4Desc"),
    },
  ];

  const steps: { step: string; title: string; description: string; bullets?: string[]; footer?: string }[] = [
    {
      step: t("individuals.step1Label"),
      title: t("individuals.step1Title"),
      description: t("individuals.step1Desc"),
    },
    {
      step: t("individuals.step2Label"),
      title: t("individuals.step2Title"),
      description: t("individuals.step2Desc"),
    },
    {
      step: t("individuals.step3Label"),
      title: t("individuals.step3Title"),
      description: t("individuals.step3Desc"),
      footer: t("individuals.step3Footer"),
    },
  ];

  const plans = [
    {
      name: t("pricing.essentialsName"),
      price: "$25",
      period: t("pricing.perMonth"),
      subtitle: t("pricing.essentialsSubtitle"),
      services: ["Telemedicine", "Dermatology"].map(n => makeServiceT(n, t)),
      popular: false,
    },
    {
      name: t("pricing.advantageName"),
      price: "$35",
      period: t("pricing.perMonth"),
      subtitle: t("pricing.advantageSubtitle"),
      services: ["Telemedicine", "Dermatology", "Behavioral Health", "Pharmacy Savings", "Healthcare Concierge"].map(n => makeServiceT(n, t)),
      popular: false,
    },
    {
      name: t("pricing.premierName"),
      price: "$45",
      period: t("pricing.perMonth"),
      subtitle: t("pricing.premierSubtitle"),
      services: ["Telemedicine", "Dermatology", "Behavioral Health", "Pharmacy Savings", "Healthcare Concierge", "Lab Testing", "Wellness Coaching", "Best Price MRI/Imaging", "Medical Bill Negotiations", "Medical Bill Financing"].map(n => makeServiceT(n, t)),
      popular: false,
    },
  ];

  const testimonials = [
    { text: t("individuals.testimonial1Text"), name: t("individuals.testimonial1Name"), role: t("individuals.testimonial1Role"), avatar: "JM", rating: 5 },
    { text: t("individuals.testimonial2Text"), name: t("individuals.testimonial2Name"), role: t("individuals.testimonial2Role"), avatar: "CR", rating: 5 },
    { text: t("individuals.testimonial3Text"), name: t("individuals.testimonial3Name"), role: "", avatar: "WF", rating: 5 },
  ];

  const faqs = [
    { question: t("individuals.faq1Q"), answer: t("individuals.faq1A") },
    { question: t("individuals.faq2Q"), answer: t("individuals.faq2A") },
    { question: t("individuals.faq3Q"), answer: t("individuals.faq3A") },
    { question: t("individuals.faq4Q"), answer: t("individuals.faq4A") },
    { question: t("individuals.faq5Q"), answer: t("individuals.faq5A") },
  ];

  return (
    <div className="min-h-screen">
      <Navbar />

      <PageHero
        title={t("individuals.heroTitle")}
        subtitle={t("individuals.heroSubtitle")}
        backgroundImage={familyHealthcare}
      >

        <a href="https://support.legacy-hc.com/widget/form/TgnMv3pkwXzQzfAvbwXs" target="_blank" rel="noopener noreferrer">
          <Button className="bg-accent-blue hover:bg-accent-blue/90 text-white rounded-full px-8 py-4 font-button text-base cta-hover cta-hover-glow mt-6">
            {t("individuals.viewPlans")} <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </a>
      </PageHero>

      {/* Pricing */}
      <section className="py-12 px-6 lg:px-12 bg-white" id="pricing">
        <div className="max-w-[1100px] mx-auto">
          <AnimatedSection>
            <h2 className="font-heading font-bold text-2xl lg:text-[32px] text-foreground text-center mb-10">{t("individuals.pricingHeading")}</h2>
          </AnimatedSection>
          <PricingCards plans={plans} ctaAction={() => window.open("https://support.legacy-hc.com/widget/form/TgnMv3pkwXzQzfAvbwXs", "_blank")} />
          <AnimatedSection delay={300}>
            <div className="text-center mt-8">
              <a href="https://support.legacy-hc.com/widget/form/TgnMv3pkwXzQzfAvbwXs" target="_blank" rel="noopener noreferrer" className="block w-full max-w-[327px] mx-auto">
                <Button className="bg-accent-blue hover:bg-accent-blue/90 text-white rounded-full px-6 py-3 font-button text-sm cta-hover w-full">
                  {t("individuals.startSubscription")} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </a>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* How It Works — Vertical Timeline */}
      <section className="relative py-20 px-6 lg:px-12 bg-secondary overflow-hidden">
        {/* Decorative gradient circles */}
        <div className="pointer-events-none absolute -top-24 -left-24 w-[420px] h-[420px] rounded-full opacity-[0.07]" style={{ background: "radial-gradient(circle, hsl(var(--accent-blue)), transparent 70%)" }} />
        <div className="pointer-events-none absolute top-1/2 -right-32 w-[500px] h-[500px] rounded-full opacity-[0.06]" style={{ background: "radial-gradient(circle, hsl(var(--primary)), transparent 70%)" }} />
        <div className="pointer-events-none absolute -bottom-20 left-1/3 w-[360px] h-[360px] rounded-full opacity-[0.05]" style={{ background: "radial-gradient(circle, hsl(var(--accent-blue)), transparent 70%)" }} />
        <div className="max-w-[1100px] mx-auto">
          <AnimatedSection>
            <h2 className="font-heading font-bold text-2xl lg:text-[36px] text-foreground text-center mb-16">{t("individuals.howItWorks")}</h2>
          </AnimatedSection>

          {/* Timeline container */}
          <div className="relative">

            {/* ── MOBILE timeline: left-side numbered steps ── */}
            <div className="lg:hidden flex flex-col">
              {steps.map((step, index) => (
                <AnimatedSection key={`m-${step.step}`} delay={index * 150}>
                  <div className="flex gap-4">
                    {/* Left: numbered circle + connecting line */}
                    <div className="flex flex-col items-center flex-shrink-0 w-9">
                      <div
                        className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold shadow-md flex-shrink-0"
                        style={{ background: "linear-gradient(135deg, hsl(var(--accent-blue)), hsl(var(--primary)))" }}
                      >
                        {index + 1}
                      </div>
                      {index < steps.length - 1 && (
                        <div
                          className="w-0.5 flex-1 mt-3 mb-1 min-h-[32px] rounded-full opacity-25"
                          style={{ background: "linear-gradient(to bottom, hsl(var(--accent-blue)), hsl(var(--primary)))" }}
                        />
                      )}
                    </div>
                    {/* Right: label + card */}
                    <div className="flex-1 pb-6">
                      <p
                        className="font-button text-xs font-semibold uppercase tracking-widest mb-2"
                        style={{ color: "hsl(var(--accent-blue))" }}
                      >
                        {step.step}
                      </p>
                      <div className="bg-white rounded-2xl p-5 shadow-sm border border-foreground/[0.06]">
                        <h3 className="font-heading font-bold text-base text-foreground mb-2 leading-snug">{step.title}</h3>
                        <p className="font-body text-sm text-foreground/65 leading-relaxed">{step.description}</p>
                        {step.bullets && (
                          <ul className="mt-3 space-y-1.5">
                            {step.bullets.map((bullet) => (
                              <li key={bullet} className="font-body text-sm text-foreground/65 flex items-start gap-2">
                                <span className="text-accent-blue mt-0.5 flex-shrink-0">•</span>{bullet}
                              </li>
                            ))}
                          </ul>
                        )}
                        {step.footer && (
                          <p className="font-body text-xs text-foreground/50 leading-relaxed mt-3 italic">{step.footer}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </AnimatedSection>
              ))}
            </div>

            {/* ── DESKTOP timeline ── */}
            <div className="hidden lg:block relative">
              {/* Center vertical line */}
              <div className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-accent-blue via-primary to-accent-blue opacity-30" />
              <div className="flex flex-col gap-8">
                {steps.map((step, index) => {
                  const contentRight = index % 2 === 0;
                  return (
                    <AnimatedSection key={step.step} delay={index * 150}>
                      {/* 3-column grid: left content | circle | right content */}
                      <div className="grid items-start" style={{ gridTemplateColumns: "1fr 64px 1fr" }}>

                        {/* LEFT content area */}
                        <div className="pr-10">
                          {!contentRight ? (
                            <div>
                              <div className="flex justify-end mb-3">
                                <div className="px-5 py-2 rounded-full text-white font-button text-sm font-semibold shadow-md w-fit" style={{ background: "linear-gradient(135deg, hsl(var(--accent-blue)), hsl(var(--primary)))" }}>{step.step}</div>
                              </div>
                              <div className="bg-white rounded-2xl p-5 shadow-sm border border-foreground/[0.06] hover:-translate-y-1 hover:shadow-md transition-all duration-300">
                                <h3 className="font-heading font-bold text-[17px] text-foreground mb-2 leading-snug">{step.title}</h3>
                                <p className="font-body text-sm text-foreground/65 leading-relaxed">{step.description}</p>
                                {step.bullets && (<ul className="mt-3 space-y-1.5">{step.bullets.map((bullet) => (<li key={bullet} className="font-body text-sm text-foreground/65 flex items-start gap-2"><span className="text-primary mt-0.5 flex-shrink-0">•</span>{bullet}</li>))}</ul>)}
                                {step.footer && (<p className="font-body text-xs text-foreground/50 leading-relaxed mt-3 italic">{step.footer}</p>)}
                              </div>
                            </div>
                          ) : <div />}
                        </div>

                        {/* CENTER: circle on the line */}
                        <div className="flex justify-center pt-2">
                          <div
                            className="w-5 h-5 rounded-full border-2 border-white shadow-md z-10 flex-shrink-0"
                            style={{ background: "linear-gradient(135deg, hsl(var(--accent-blue)), hsl(var(--primary)))" }}
                          />
                        </div>

                        {/* RIGHT content area */}
                        <div className="pl-10">
                          {contentRight ? (
                            <div>
                              <div className="mb-3">
                                <div className="px-5 py-2 rounded-full text-white font-button text-sm font-semibold shadow-md w-fit" style={{ background: "linear-gradient(135deg, hsl(var(--accent-blue)), hsl(var(--primary)))" }}>{step.step}</div>
                              </div>
                              <div className="bg-white rounded-2xl p-5 shadow-sm border border-foreground/[0.06] hover:-translate-y-1 hover:shadow-md transition-all duration-300">
                                <h3 className="font-heading font-bold text-[17px] text-foreground mb-2 leading-snug">{step.title}</h3>
                                <p className="font-body text-sm text-foreground/65 leading-relaxed">{step.description}</p>
                                {step.bullets && (<ul className="mt-3 space-y-1.5">{step.bullets.map((bullet) => (<li key={bullet} className="font-body text-sm text-foreground/65 flex items-start gap-2"><span className="text-accent-blue mt-0.5 flex-shrink-0">•</span>{bullet}</li>))}</ul>)}
                                {step.footer && (<p className="font-body text-xs text-foreground/50 leading-relaxed mt-3 italic">{step.footer}</p>)}
                              </div>
                            </div>
                          ) : <div />}
                        </div>

                      </div>
                    </AnimatedSection>
                  );
                })}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Want to See It in Action? — Featured Videos */}
      <section className="py-16 lg:py-20 px-6 lg:px-12 bg-white">
        <div className="max-w-[1100px] mx-auto">
          <AnimatedSection>
            <div className="text-center mb-3">
              <h2 className="font-heading font-bold text-2xl lg:text-[36px] text-foreground">
                Want to See It in Action?
              </h2>
              <p className="font-body text-base text-foreground/70 max-w-2xl mx-auto mt-3">
                Take a quick look inside the Legacy membership and see how easy it is to access care.
              </p>
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
            {featuredVideos.map((video, i) => (
              <AnimatedSection key={video.id} delay={i * 100}>
                <FeaturedVideoCard video={video} />
              </AnimatedSection>
            ))}
          </div>

          <AnimatedSection delay={400}>
            <div className="text-center mt-10">
              <Link href="/how-it-works">
                <Button variant="outline" className="border-accent-blue text-accent-blue hover:bg-accent-blue hover:text-white rounded-full px-6 py-3 font-button text-sm cta-hover">
                  See All Walkthroughs <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* About Legacy */}
      <section className="py-20 px-6 lg:px-12 bg-white">
        <div className="max-w-[1100px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <AnimatedSection>
              <div className="rounded-2xl overflow-hidden h-[360px] lg:h-[480px]">
                <img src={familyIndividuals} alt="Family waving at laptop during virtual healthcare visit" className="w-full h-full object-cover object-top" loading="lazy" />
              </div>
            </AnimatedSection>
            <AnimatedSection delay={100}>
              <h2 className="font-heading font-bold text-2xl lg:text-[36px] text-foreground mb-3">{t("individuals.aboutHeading")}</h2>
              <p className="font-body text-sm text-foreground/80 leading-relaxed mb-6">{t("individuals.aboutDesc")}</p>
              <h3 className="font-heading font-bold text-base text-foreground mb-3">{t("individuals.whetherYou")}</h3>
              <ul className="space-y-3">
                {[
                  t("individuals.whetherItem1"),
                  t("individuals.whetherItem2"),
                  t("individuals.whetherItem3"),
                  t("individuals.whetherItem4"),
                ].map((item) => (
                  <li key={item} className="font-body text-sm text-foreground/80 flex items-start gap-2">
                    <Check className="w-4 h-4 text-accent-blue mt-0.5 flex-shrink-0" />{item}
                  </li>
                ))}
              </ul>
            </AnimatedSection>
          </div>
          {/* Feature Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {features.map((feature, i) => (
              <AnimatedSection key={feature.title} delay={i * 100}>
                <div className="group bg-white rounded-2xl p-6 hover:shadow-lg transition-all duration-300 hover:bg-accent-blue cursor-pointer h-full">
                  <div className="w-12 h-12 bg-accent-blue/10 group-hover:bg-white/20 rounded-xl flex items-center justify-center mb-4 transition-colors duration-300">
                    <feature.icon className="w-6 h-6 text-accent-blue group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="font-heading font-bold text-sm mb-2 text-foreground group-hover:text-white transition-colors duration-300">{feature.title}</h3>
                  <p className="font-body text-xs text-foreground/70 group-hover:text-white/80 leading-relaxed transition-colors duration-300">{feature.description}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Why Families Choose */}
      <section className="py-20 px-6 lg:px-12 bg-secondary">
        <div className="max-w-[900px] mx-auto">
          <AnimatedSection>
            <h2 className="font-heading font-bold text-2xl lg:text-[36px] text-foreground text-center mb-12">{t("individuals.whyFamilies")}</h2>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <AnimatedSection>
              <div className="bg-surface-card rounded-2xl overflow-hidden h-full flex flex-col">
                <div className="h-56 overflow-hidden">
                  <img src={withoutLegacyImg} alt="Frustrated person in crowded waiting room" className="w-full h-full object-cover object-top" />
                </div>
                <div className="p-8 flex-1">
                  <h3 className="font-heading font-bold text-lg text-foreground mb-2">{t("individuals.traditionalCare")}</h3>
                  <ul className="space-y-3">
                    <li className="font-body text-sm text-foreground/70 flex items-start gap-2"><span className="text-destructive mt-0.5">✕</span>{t("individuals.trad1")}</li>
                    <li className="font-body text-sm text-foreground/70 flex items-start gap-2"><span className="text-destructive mt-0.5">✕</span>{t("individuals.trad2")}</li>
                    <li className="font-body text-sm text-foreground/70 flex items-start gap-2"><span className="text-destructive mt-0.5">✕</span>{t("individuals.trad3")}</li>
                    <li className="font-body text-sm text-foreground/70 flex items-start gap-2"><span className="text-destructive mt-0.5">✕</span>{t("individuals.trad4")}</li>
                    <li className="font-body text-sm text-foreground/70 flex items-start gap-2"><span className="text-destructive mt-0.5">✕</span>{t("individuals.trad5")}</li>
                  </ul>
                </div>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={150}>
              <div className="bg-deep-blue rounded-2xl overflow-hidden h-full flex flex-col">
                <div className="h-56 overflow-hidden">
                  <img src={withLegacyImg} alt="Happy family on virtual doctor call" className="w-full h-full object-cover object-top" />
                </div>
                <div className="p-8 flex-1">
                  <h3 className="font-heading font-bold text-lg text-white mb-4">{t("individuals.withLegacy")}</h3>
                  <ul className="space-y-3">
                    {[t("individuals.with1"), t("individuals.with2"), t("individuals.with3"), t("individuals.with4"), t("individuals.with5")].map((item) => (
                      <li key={item} className="font-body text-sm text-white/90 flex items-start gap-2"><Check className="w-4 h-4 text-accent-blue mt-0.5 flex-shrink-0" />{item}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </AnimatedSection>
          </div>
          <AnimatedSection>
            <p className="font-body text-sm text-foreground/60 text-center mt-6">{t("individuals.disclaimer")}</p>
          </AnimatedSection>
        </div>
      </section>

      {/* Mini FAQ */}
      <section className="py-12 px-6 lg:px-12 bg-white">
        <div className="max-w-[800px] mx-auto">
          <AnimatedSection>
            <h2 className="font-heading font-bold text-2xl lg:text-[32px] text-foreground text-center mb-2">{t("individuals.faqHeading")}</h2>
            <p className="font-body text-base text-foreground/70 text-center mb-8">{t("individuals.faqSubtitle")}</p>
          </AnimatedSection>
          <AnimatedSection delay={100}>
            <Accordion type="single" collapsible className="mb-8">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`faq-${index}`} className="bg-surface-card rounded-xl mb-3 px-5 border-l-4 border-l-accent-blue border-t-0 border-r-0 border-b-0 shadow-sm">
                  <AccordionTrigger className="font-heading font-medium text-sm text-foreground hover:text-accent-blue hover:no-underline py-4 text-left">{faq.question}</AccordionTrigger>
                  <AccordionContent className="font-body text-xs text-foreground/70 pb-4 text-left">{faq.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </AnimatedSection>
          <AnimatedSection delay={200}>
            <div className="text-center">
              <Link href="/faqs">
                <Button variant="outline" className="border-accent-blue text-accent-blue hover:bg-accent-blue hover:text-white rounded-full px-6 py-3 font-button text-sm cta-hover">
                  {t("individuals.importantFaqs")} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-6 lg:px-12 bg-secondary">
        <div className="max-w-[1100px] mx-auto">
          <div className="text-center mb-10">
             <AnimatedSection>
              <h2 className="font-heading font-bold text-2xl lg:text-[36px] text-foreground">
                {t("individuals.testimonialsHeading")}
              </h2>
            </AnimatedSection>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, i) => (
              <AnimatedSection key={testimonial.name} delay={i * 100}>
                <div className="flex flex-col h-full">
                  <div className="bg-surface-card rounded-xl p-6 shadow-sm flex-1">
                    <div className="flex gap-1 mb-3">
                      {[...Array(testimonial.rating)].map((_, j) => (
                        <Star key={j} className="w-4 h-4 text-accent-blue fill-accent-blue" />
                      ))}
                    </div>
                    <p className="font-body text-sm text-foreground/80 leading-relaxed">
                      "{testimonial.text}"
                    </p>
                  </div>
                  <div className="flex items-center gap-3 mt-4 px-2">
                    <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                      <span className="font-heading font-bold text-xs text-white">{testimonial.avatar}</span>
                    </div>
                    <div>
                      <p className="font-heading font-medium text-sm text-foreground">{testimonial.name}</p>
                      {testimonial.role && <p className="font-body text-xs text-foreground/60">{testimonial.role}</p>}
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20 px-6 lg:px-12 bg-deep-blue text-white text-center">
        <AnimatedSection className="max-w-[700px] mx-auto">
          <h2 className="font-heading font-bold text-2xl lg:text-[36px] mb-3">{t("individuals.bottomCtaHeading")}</h2>
          <p className="font-body text-base text-white/80 mb-3">{t("individuals.bottomCtaSubtitle")}</p>
          <p className="font-heading font-semibold text-lg text-white/90 mb-8">{t("individuals.bottomCtaCta")}</p>
          <a href="https://support.legacy-hc.com/widget/form/TgnMv3pkwXzQzfAvbwXs" target="_blank" rel="noopener noreferrer">
            <Button className="bg-accent-blue hover:bg-accent-blue/90 text-white rounded-full px-8 py-4 font-button text-base cta-hover">
              {t("individuals.startSubscription")} <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </a>
        </AnimatedSection>
      </section>

      <Footer />
    </div>
  );
};

export default Individuals;
